export const ATTENDEE_EVENT_MOMENTS = [
  "ON_CHECK_IN",
  "ON_ORDER_COMPLETED",
] as const;

export const SESSION_MOMENTS = [
  "BEFORE_SESSION_START",
  "AFTER_SESSION_START",
] as const;

export const EVENT_TIME_MOMENTS = [
  "BEFORE_EVENT_START",
  "AFTER_EVENT_END",
] as const;

export type CommunicationTriggerMomentName =
  | (typeof ATTENDEE_EVENT_MOMENTS)[number]
  | (typeof SESSION_MOMENTS)[number]
  | (typeof EVENT_TIME_MOMENTS)[number];

export type CommunicationOffsetUnitName = "MINUTES" | "HOURS" | "DAYS";

const MS = {
  MINUTES: 60_000,
  HOURS: 3_600_000,
  DAYS: 86_400_000,
} as const;

export function isAttendeeEventMoment(
  moment: string | null | undefined
): moment is (typeof ATTENDEE_EVENT_MOMENTS)[number] {
  return ATTENDEE_EVENT_MOMENTS.includes(
    moment as (typeof ATTENDEE_EVENT_MOMENTS)[number]
  );
}

export function isSessionMoment(
  moment: string | null | undefined
): moment is (typeof SESSION_MOMENTS)[number] {
  return SESSION_MOMENTS.includes(moment as (typeof SESSION_MOMENTS)[number]);
}

export function isEventTimeMoment(
  moment: string | null | undefined
): moment is (typeof EVENT_TIME_MOMENTS)[number] {
  return EVENT_TIME_MOMENTS.includes(
    moment as (typeof EVENT_TIME_MOMENTS)[number]
  );
}

export function offsetMilliseconds(
  value: number | null | undefined,
  unit: CommunicationOffsetUnitName | null | undefined
): number {
  const amount = value ?? 0;
  return amount * MS[unit ?? "MINUTES"];
}

export function applyOffset(
  anchor: Date,
  direction: "BEFORE" | "AFTER",
  value: number | null | undefined,
  unit: CommunicationOffsetUnitName | null | undefined
): Date {
  const delta = offsetMilliseconds(value, unit);
  return new Date(anchor.getTime() + (direction === "BEFORE" ? -delta : delta));
}

export function calculateRelativeSendAt(input: {
  moment: CommunicationTriggerMomentName | null | undefined;
  offsetValue?: number | null;
  offsetUnit?: CommunicationOffsetUnitName | null;
  eventStart?: Date | null;
  eventEnd?: Date | null;
  sessionStart?: Date | null;
}): Date | null {
  if (!input.moment || isAttendeeEventMoment(input.moment)) return null;

  if (input.moment === "BEFORE_SESSION_START") {
    if (!input.sessionStart) return null;
    return applyOffset(
      input.sessionStart,
      "BEFORE",
      input.offsetValue,
      input.offsetUnit
    );
  }
  if (input.moment === "AFTER_SESSION_START") {
    if (!input.sessionStart) return null;
    return applyOffset(
      input.sessionStart,
      "AFTER",
      input.offsetValue,
      input.offsetUnit
    );
  }
  if (input.moment === "BEFORE_EVENT_START") {
    if (!input.eventStart) return null;
    return applyOffset(
      input.eventStart,
      "BEFORE",
      input.offsetValue,
      input.offsetUnit
    );
  }
  if (!input.eventEnd) return null;
  return applyOffset(input.eventEnd, "AFTER", input.offsetValue, input.offsetUnit);
}

export function isRelativeSendDue(
  sendAt: Date | null,
  now: Date = new Date()
): boolean {
  return sendAt != null && sendAt.getTime() <= now.getTime();
}
